/*
 * AnalyticsTabRoute.jsx
 *
 * This component is a wrapper around React Router's Route, used for the Analytics dashboard tabs.
 * It provides:
 *   - Conditional rendering of routes (via the 'condition' prop)
 *   - Deduplicated hit tracking: calls loadRecordHit only if a hit for the current URL has not already been recorded
 *   - Avoids logging hits for redirect routes (e.g., when rendering a <Redirect />), to prevent double hits on initial navigation/redirects
 *   - Manual referrer tracking for SPA navigation: passes the previous URL as the referrer for hit tracking
 *
 * This ensures that only user-facing Analytics tab navigations are logged as hits, and not intermediate redirects or duplicate renders.
 */
import React from 'react';
import { Route } from 'react-router-dom';
import { loadRecordHit } from 'js/utils/loadRecordHit';

// Global variable to track the last Analytics URL for SPA referrer
if (typeof window !== 'undefined' && !window.__lastAnalyticsUrl) {
  window.__lastAnalyticsUrl = window.location.href;
}

export const AnalyticsTabRoute = ({
  condition = true,
  component: Component,
  ...routeProps
}) =>
  condition ? (
    <Route
      {...routeProps}
      render={(props) => {
        const element = <Component {...props} />;
        if (
          element &&
          element.props &&
          element.props.history &&
          element.props.history.action === 'REPLACE'
        ) {
          return element;
        }

        if (
          window.Aedu &&
          window.Aedu.hit_data &&
          window.Aedu.hit_data.url === window.location.href
        ) {
          window.__lastAnalyticsUrl = window.location.href;
          return element;
        }
        // Use the tracked last URL as the referrer for SPA navigation
        const referrer = window.__lastAnalyticsUrl || document.referrer;
        loadRecordHit(true, referrer);
        window.__lastAnalyticsUrl = window.location.href;
        return element;
      }}
    />
  ) : null;
